// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Tooltip from "@insite/mobius/Tooltip";
import setSoldToValue from "@store/Framework/Store/Greif/Pages/ContactUs/Handlers/SetSoldToValue";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    tooltipText = "tooltipText",
    isRequired = "isRequired",
}

export interface GreifSoldToCustomerNumberProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.tooltipText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    soldTo: state.greif.pages.contactUs.soldTo,
});

const mapDispatchToProps = {
    setSoldToValue,
};

type Props = GreifSoldToCustomerNumberProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface GreifSoldToCustomerNumberStyles {
    soldToTextField?: TextFieldPresentationProps;
}

export const greifSoldToCustomerNumberStyles: GreifSoldToCustomerNumberStyles = {};

const styles = greifSoldToCustomerNumberStyles;

const GreifSoldToCustomerNumber: FC<Props> = ({ fields, soldTo, setSoldToValue }) => {
    const { label, hintText, tooltipText, isRequired } = fields;
    const contactFormContext = useContext(ContactFormContext);
    const [soldToErrorMessage, setSoldToErrorMessage] = useState("");
    const [hasValidated, setHasValidated] = useState(false);

    useEffect(() => {
        contactFormContext.validators.greifSoldToCustomerNumber = validateSoldTo;
    }, [soldTo]);

    useEffect(() => {
        if (hasValidated) {
            validateSoldTo();
        }
    }, [soldTo, hasValidated]);

    useEffect(() => {
        contactFormContext.validators.greifSoldToCustomerNumber = validateSoldTo;
    }, [isRequired]);

    const validateSoldTo = (value?: string) => {
        setHasValidated(true);
        const effectiveValue = value ?? soldTo ?? "";
        const errorMessage = isRequired && !effectiveValue ? siteMessage("Field_Required", label).toString() : "";
        setSoldToErrorMessage(errorMessage);
        return !errorMessage;
    };

    const soldToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSoldToValue({ value: event.target.value });
        if (hasValidated) {
            validateSoldTo(event.target.value);
        }
    };

    return (
        <TextField
            {...styles.soldToTextField}
            label={
                <>
                    {label} {!!tooltipText?.trim() && <Tooltip text={tooltipText} />}
                </>
            }
            required={isRequired}
            placeholder={hintText}
            value={soldTo || ""}
            error={soldToErrorMessage}
            onChange={soldToChangeHandler}
            type="number"
            data-test-selector="contact-us-greif-sold-to-customer-number"
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GreifSoldToCustomerNumber),
    definition: {
        displayName: "Greif Sold-to Customer Number",
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Greif Sold to Customer Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter Greif Sold to Customer Number",
                isRequired: true,
            },
            {
                name: fields.tooltipText,
                displayName: "Tooltip Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "You can find your Greif Sold to Customer Number on Greif Invoices",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
